import { BrowserAgent, Ajax, JSErrors, Metrics, PageAction, PageViewEvent, PageViewTiming, SessionTrace, Spa } from '@newrelic/browser-agent';

const getNewRelicConfig = (templateConfig: any): any => {
  const variables = templateConfig?.pages?.features?.newRelic || {};
  const shouldApplyMasking = !variables.unmaskSensitiveFields;
  const options = {
    init: {
      session_replay: {
        enabled: true,
        block_selector: 'nr-block',
        mask_text_selector: 'nr-mask',
        sampling_rate: 100.0,
        error_sampling_rate: 100.0,
        mask_all_inputs: false,
        collect_fonts: true,
        inline_images: false,
        inline_stylesheet: true,
        fix_stylesheets: true,
        preload: true,
        mask_input_options: shouldApplyMasking ? {} : { password: false },
      },
      distributed_tracing: {
        enabled: true,
      },
      privacy: {
        cookies_enabled: true,
        maskAllInputs: false,
      },
      ajax: {
        deny_list: ['bam.nr-data.net'],
      },
    },
    loader_config: {
      accountID: String(variables.trustKey),
      trustKey: String(variables.trustKey),
      agentID: String(variables.agentID),
      licenseKey: String(variables.licenseKey),
      applicationID: String(variables.applicationID),
    },
    info: {
      beacon: String(variables.beacon),
      errorBeacon: String(variables.errorBeacon),
      licenseKey: String(variables.licenseKey),
      applicationID: String(variables.applicationID),
      sa: String(variables.sa),
    },
    features: [Ajax, JSErrors, Metrics, PageAction, PageViewEvent, PageViewTiming, SessionTrace, Spa],
  };
  return options;
};

let newRelicAgent: BrowserAgent | null = null;

export const initializeNewRelic = (templateConfig: any, userEmail?: string) => {
  if (!newRelicAgent) {
    try {
      const newRelicConfig = getNewRelicConfig(templateConfig);
      newRelicAgent = new BrowserAgent(newRelicConfig);

      if (userEmail) {
        newRelicAgent.setUserId(userEmail);
      }
    } catch (error) {
      console.error('[NewRelic] Error initializing agent:', error);
    }
  } else {
    console.warn('[NewRelic] Agent already initialized');
  }
};

export const updateNewRelicUser = (userEmail: string) => {
  if (newRelicAgent) {
    try {
      newRelicAgent.setUserId(userEmail);
    } catch (error) {
      console.error('[NewRelic] Error updating user ID:', error);
    }
  } else {
    console.warn('[NewRelic] Cannot update user ID - agent not initialized');
  }
};

export const setCustomNewRelicAttribute = (customNewRelicAttribute: string, customNewRelicAttributeValue: string) => {
  if (newRelicAgent && customNewRelicAttribute != null) {
    try {
      newRelicAgent.setCustomAttribute(customNewRelicAttribute, customNewRelicAttributeValue);
    } catch (error) {
      console.error('[NewRelic] Error updating customNewRelicAttribute: ' + customNewRelicAttribute, error);
    }
  } else {
    console.warn('[NewRelic] Cannot update customNewRelicAttribute - agent not initialized');
  }
};

export const getNewRelicAgent = () => {
  return newRelicAgent;
};

export const logConsent = (consentValue: boolean, userEmail?: string) => {
  const consentData: any = {
    consentValue,
    consentTimestamp: new Date().toISOString(),
  };

  if (userEmail) {
    consentData.borrowerName = userEmail;
  }

  updateNewRelicMetadata(consentData);
  trackCustomEvent('consent_given', consentData);
};

export const updateNewRelicMetadata = (metadata: any) => {
  if (!newRelicAgent) {
    console.warn('[NewRelic] Cannot update metadata - agent not initialized');
    return;
  }

  try {
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined) {
        newRelicAgent!.setCustomAttribute(key, value as string | number | boolean | null);
      }
    });
    console.info('[NewRelic] Metadata updated:', metadata);
  } catch (error) {
    console.error('[NewRelic] Error updating metadata:', error);
  }
};

export const trackCustomEvent = (eventName: string, attributes: Record<string, unknown>) => {
  if (newRelicAgent) {
    try {
      newRelicAgent.noticeError(new Error(eventName), attributes);
      console.info('[NewRelic] Custom event tracked:', eventName, attributes);
    } catch (error) {
      console.error('[NewRelic] Error tracking custom event:', error);
    }
  } else {
    console.warn('[NewRelic] Cannot track custom event - agent not initialized');
  }
};
